/**
 * The schedule macOS reads from a dynamic wallpaper (2026-10-12): a binary property list, base64 in
 * an XMP attribute of the `apple_desktop` namespace on the primary picture. Three kinds, the three
 * the Wallpaper settings know:
 *
 *   apple_desktop:h24    time of day   { ap: { d, l }, ti: [ { i, t } ] }   t: the share of the day, 0..1
 *   apple_desktop:solar  sun position  { ap: { d, l }, si: [ { i, a, z } ] } a: altitude, z: azimuth (degrees)
 *   apple_desktop:apr    appearance    { d, l }
 *
 * i is a picture's index in the file, d and l the pictures for Dark and Light mode.
 */

export type PlistValue = number | string | PlistValue[] | { [ key: string ]: PlistValue };

/** a real (not an integer) in the property list, whatever its value */
export class PlistReal {
  constructor( readonly value: number ) {
  }
}

type Node = PlistValue | PlistReal;

/** `bplist00`: what Foundation's binary PropertyListEncoder writes and what the desktop reads */
export function binaryPlist( root: Node ): Uint8Array {
  const objects: number[][] = [];
  const add = ( value: Node ): number => {
    const index = objects.length;
    objects.push( [] );
    const out: number[] = [];
    if ( value instanceof PlistReal ) {
      out.push( 0x23, ...float64( value.value ) );
    } else if ( typeof value === 'number' ) {
      if ( Number.isInteger( value ) ) {
        out.push( ...integer( value ) );
      } else {
        out.push( 0x23, ...float64( value ) );
      }
    } else if ( typeof value === 'string' ) {
      if ( !/^[\x00-\x7f]*$/.test( value ) ) throw new Error( 'plist: ASCII strings only' );
      out.push( ...marker( 0x50, value.length ) );
      for ( let i = 0; i < value.length; i++ ) out.push( value.charCodeAt( i ) );
    } else if ( Array.isArray( value ) ) {
      const refs = value.map( add );
      out.push( ...marker( 0xa0, refs.length ), ...refs.map( r => -1 - r ) );
    } else {
      // keys sorted, as Foundation writes them
      const keys = Object.keys( value ).sort();
      const keyRefs = keys.map( add );
      const valueRefs = keys.map( k => add( value[ k ] ) );
      out.push( ...marker( 0xd0, keys.length ), ...keyRefs.map( r => -1 - r ), ...valueRefs.map( r => -1 - r ) );
    }
    objects[ index ] = out;
    return index;
  };
  add( root );

  // object references were written as -1 - index, sized now that the count is known
  const refSize = objects.length < 256 ? 1 : 2;
  const bytes: number[] = [ 0x62, 0x70, 0x6c, 0x69, 0x73, 0x74, 0x30, 0x30 ];
  const offsets: number[] = [];
  for ( const obj of objects ) {
    offsets.push( bytes.length );
    for ( const b of obj ) {
      if ( b < 0 ) {
        const ref = -1 - b;
        if ( refSize === 2 ) bytes.push( ref >> 8 );
        bytes.push( ref & 255 );
      } else {
        bytes.push( b );
      }
    }
  }
  const tableAt = bytes.length;
  const offsetSize = tableAt < 256 ? 1 : tableAt < 65536 ? 2 : 4;
  for ( const off of offsets ) bytes.push( ...be( off, offsetSize ) );
  bytes.push( 0, 0, 0, 0, 0, 0, offsetSize, refSize, ...be( objects.length, 8 ), ...be( 0, 8 ), ...be( tableAt, 8 ) );
  return Uint8Array.from( bytes );
}

function be( v: number, size: number ): number[] {
  const out: number[] = [];
  for ( let i = size - 1; i >= 0; i-- ) out.push( Math.floor( v / Math.pow( 256, i ) ) & 255 );
  return out;
}

function integer( v: number ): number[] {
  if ( v < 0 ) {
    // a negative integer is always 8 bytes, two's complement
    const hi = Math.floor( v / 4294967296 ) >>> 0, lo = ( v >>> 0 );
    return [ 0x13, ...be( hi, 4 ), ...be( lo, 4 ) ];
  }
  if ( v < 256 ) return [ 0x10, v ];
  if ( v < 65536 ) return [ 0x11, ...be( v, 2 ) ];
  if ( v < 4294967296 ) return [ 0x12, ...be( v, 4 ) ];
  return [ 0x13, ...be( v, 8 ) ];
}

function float64( v: number ): number[] {
  const view = new DataView( new ArrayBuffer( 8 ) );
  view.setFloat64( 0, v );
  return Array.from( new Uint8Array( view.buffer ) );
}

/** the type nibble with its count; 15 and up spill into an integer object after it */
function marker( type: number, count: number ): number[] {
  return count < 15 ? [ type | count ] : [ type | 15, ...integer( count ) ];
}

function base64( bytes: Uint8Array ): string {
  let s = '';
  for ( let i = 0; i < bytes.length; i++ ) s += String.fromCharCode( bytes[ i ] );
  return btoa( s );
}

export interface ScheduleFrame {
  /** the picture's index in the file */
  index: number;
  /** the local hour it stands for, 0..24 */
  hour: number;
  /** the sun at that hour, degrees */
  altitude: number;
  azimuth: number;
}

export type ScheduleKind = 'h24' | 'solar' | 'apr';

/** the property list for one kind of schedule */
export function schedulePlist( kind: ScheduleKind, frames: ScheduleFrame[], light: number, dark: number ): Uint8Array {
  const ap = { d: dark, l: light };
  if ( kind === 'apr' ) {
    return binaryPlist( ap );
  }
  if ( kind === 'solar' ) {
    return binaryPlist( {
      ap,
      si: frames.map( f => ( { i: f.index, a: new PlistReal( round( f.altitude ) ) as any, z: new PlistReal( round( f.azimuth ) ) as any } ) ),
    } );
  }
  return binaryPlist( {
    ap,
    // by the clock, whatever order the pictures are in
    ti: [ ...frames ].sort( ( a, b ) => ( a.hour % 24 ) - ( b.hour % 24 ) )
      .map( f => ( { i: f.index, t: new PlistReal( round( ( f.hour % 24 ) / 24, 6 ) ) as any } ) ),
  } );
}

function round( v: number, digits = 4 ): number {
  const k = Math.pow( 10, digits );
  return Math.round( v * k ) / k;
}

/** the XMP packet: the property list in its apple_desktop attribute */
export function desktopXmp( kind: ScheduleKind, plist: Uint8Array ): string {
  return '<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="XMP Core 6.0.0">'
    + '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
    + `<rdf:Description rdf:about="" xmlns:apple_desktop="http://ns.apple.com/namespace/1.0/" apple_desktop:${ kind }="${ base64( plist ) }"/>`
    + '</rdf:RDF>'
    + '</x:xmpmeta>';
}
